import { PicklistOption } from "../blocks/fields/PicklistField.types";

/** The kinds of filter a list can offer. One control each, in `ListFilterBar`. */
export type ListFilterKind =
  | "text"
  | "boolean"
  | "date-range"
  /** One day, with an optional time window, for a search that only takes a single day. */
  | "day"
  | "lookup"
  | "picklist"
  /** A comparison operator and a number, as /search asks of a number. Blank is no filter. */
  | "number";

export interface ListFilterDefinition {
  /** The field the service filters on, and the key this filter's value is held under. */
  name: string;
  label: string;
  kind: ListFilterKind;
  /**
   * Row keys a text filter matches against on a client-filtered list. Defaults to the
   * filter's own name. Ignored by a list the service filters.
   */
  searchFields?: string[];
  /**
   * For a lookup filter, the entity whose records it picks from, e.g. "dwseason". The
   * service is sent the picked record's id; a list filtered in the browser compares it.
   */
  lookupEntity?: string;
  /** For a lookup filter, typed into to filter its records. */
  searchable?: boolean;  /** For a picklist filter, its fixed choices. The picked `Key` is what is sent. */
  options?: PicklistOption[];
  /**
   * For a picklist filter on a list filtered in the browser, its choices are the distinct
   * values of this field in the loaded rows, in place of `options`.
   */
  optionsFromRows?: boolean;
  /** For a picklist filter, its choices are the portal's users, each picked by email address. */
  optionsFrom?: "portal-users";
  placeholder?: string;
  /** Guidance under the control, for a filter that needs explaining. */
  helpText?: string;
  /** For a boolean filter: a switch rather than a Yes/No dropdown. Off is no filter either way. */
  toggle?: boolean;
  /** For a boolean filter on a list filtered in the browser: starts on rather than off. */
  defaultValue?: boolean;
  /** For a boolean filter on a list filtered in the browser: on hides rows whose date is past. */
  hidePast?: string;
  /** For a boolean filter on a list filtered in the browser: off keeps only rows whose field is this value. */
  onlyWhenOff?: { key: string; equals: string };
  /** Kept behind the Advanced filters toggle rather than shown beside the others. */
  advanced?: boolean;
  /**
   * A wider box than the rest of the band, for the filter a search starts from - a name
   * or an email address needs more room than a status. Omitted for the default width.
   */
  width?: ListFilterWidth;
}

/** The widths a filter box can take, beyond the band's own default. */
export type ListFilterWidth = "wide" | "wider";

/**
 * Filter values, flat and keyed by field name - a date range holds three entries,
 * `<name>-From`, `<name>-To` and `<name>-Preset`, and the first two are also how the
 * service and the query string name them. Dates are `yyyy-MM-dd`, as the date control
 * reads and writes them.
 *
 * A preset holds the range that was picked, not the days it stands for; `rangeBounds`
 * in `dateRangePresets` is what turns one into the other.
 *
 * The record status is deliberately not in here: it decides which records are read
 * rather than narrowing the ones that were, so the provider holds it separately.
 */
export type ListFilterValues = Record<string, string | boolean | undefined>;

export const DATE_SUFFIX = "-Date";
export const FROM_SUFFIX = "-From";
export const TO_SUFFIX = "-To";
export const PRESET_SUFFIX = "-Preset";
export const OPERATOR_SUFFIX = "-Operator";

/** A number filter's operator. Its number is held under the filter's own name. */
export function operatorKey(name: string) {
  return `${name}${OPERATOR_SUFFIX}`;
}

/** A day filter's day. Its `-From` and `-To` keys hold times of day, as `HH:mm`. */
export function dateKey(name: string) {
  return `${name}${DATE_SUFFIX}`;
}

export function fromKey(name: string) {
  return `${name}${FROM_SUFFIX}`;
}

export function toKey(name: string) {
  return `${name}${TO_SUFFIX}`;
}

export function presetKey(name: string) {
  return `${name}${PRESET_SUFFIX}`;
}
